import React from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, LogOut, Loader2, MessageSquare } from 'lucide-react'
import { Button, ErrorBanner, ImageViewerWithAnnotations } from '@/components'
import miniTurbineIcon from '@/assets/wind_turbine_icon.png'
import { useLanguage } from '@/contexts/LanguageContext'
import { CATEGORY_THEMES, type CategoryKey } from './constants'
import { useOperatorPhotosViewModel } from './useOperatorPhotosViewModel'
import CommentSidebar from './components/CommentSidebar'
import PhotoSidebar from './components/PhotoSidebar'


const OperatorPhotosScreen: React.FC<{ category?: CategoryKey }> = ({ category = 'operator' }) => {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const theme = CATEGORY_THEMES[category]

  const {
    turbine,
    loading,
    loadError,
    selectedPhoto,
    visiblePhotos,
    setVisiblePhotos,
    isSidebarOpen,
    isCommentsOpen,
    setIsCommentsOpen,
    user,
    isSaving,
    refreshSignal,
    imageVersion,
    photoVersions,
    currentVersionIndex,
    loadingVersions,
    versionsError,
    reloadData,
    handlePhotoSelect,
    handlePhotosLoaded,
    handleVersionChange,
    handleDeleteVersion,
    handleSaveEditedPhoto,
    handleImageError,
    handleNavigate,
    retryLoadVersions,
    dismissVersionsError,
    addCacheBust,
    logout,
  } = useOperatorPhotosViewModel()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (!turbine) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="w-full max-w-md text-center">
          {/* Yukleme HATASI ile "kayit yok" ayni sey degil: hata varsa nedeni ve
              tekrar deneme secenegi gosterilir. */}
          {loadError ? (
            <ErrorBanner message={loadError} onRetry={reloadData} className="text-left" />
          ) : (
            <p className="text-gray-600">{t.turbineNotFound}</p>
          )}
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            {t.backToDashboard}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b z-10 relative">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(`/dashboard/turbine/${turbine.id}`)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-2"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">{t.turbineDetail.photos.backToTurbineDetail}</span>
            </button>

            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                <img
                  src={miniTurbineIcon}
                  alt="Turbine Icon"
                  className="w-10 h-10 object-contain"
                />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">{turbine.name || `${turbine.city} Türbini`}</h1>
                <p className="text-sm text-gray-600">{theme.title(t)}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsCommentsOpen(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={t.turbineDetail.photos.openComments}
            >
              <MessageSquare className="w-5 h-5 text-gray-600" />
            </button>


            <Button
              onClick={logout}
              variant="outline"
              size="sm"
              className="hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
{t.turbineDetail.photos.logout}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <div className="flex h-[calc(100vh-4rem)] relative">
        {/* Photo Sidebar */}
        <PhotoSidebar
          turbineId={turbine.id}
          selectedPhoto={selectedPhoto}
          onPhotoSelect={handlePhotoSelect}
          isOpen={isSidebarOpen}
          refreshSignal={refreshSignal}
          onPhotosLoaded={handlePhotosLoaded}
          onVisiblePhotosChange={setVisiblePhotos}
          category={category}
          theme={theme}
        />

        {/* 2D Photo Viewer Area with Zoom and Annotations */}
        {/* NOTE: no `key` here — remounting on photo change would exit
            fullscreen mode while navigating with the arrows. The viewer
            resets its own state when imageUrl changes. */}
        <div className="flex-1 relative flex flex-col">
          {/* Hatalar normal akista, goruntuleyicinin USTUNDE durur. Onceki
              `absolute top-4 right-4` kutular toast taklidi yapiyor, ust uste
              binebiliyor ve akistan kopuk kaliyordu. */}
          {/* saveError BILEREK burada gosterilmez: kaydetme hatasini
              ImageViewerWithAnnotations kendi kutusunda gosteriyor (hem normal hem
              tam ekran modda). Ikisi birden basilinca kullanici ayni metni iki kez
              goruyordu. */}
          {(loadError || versionsError) && (
            <div className="p-3 space-y-2">
              <ErrorBanner message={loadError} onRetry={reloadData} />
              {/* Fotograf gorunmeye devam ettigi icin versiyon listesi sorunu
                  UYARI tonunda: islev kisitli, ekran calisiyor. */}
              <ErrorBanner
                message={versionsError}
                tone="warning"
                onRetry={retryLoadVersions}
                onDismiss={dismissVersionsError}
              />
            </div>
          )}

          <ImageViewerWithAnnotations
            imageUrl={selectedPhoto ? addCacheBust(selectedPhoto.url, imageVersion) : null}
            imageName={selectedPhoto?.originalName || ''}
            imageInfo={[
              selectedPhoto?.year && `${selectedPhoto.year}`,
              selectedPhoto?.size && `${(selectedPhoto.size / 1024 / 1024).toFixed(1)} MB`,
              selectedPhoto?.uploader_name
            ].filter(Boolean).join(' • ')}
            onSave={handleSaveEditedPhoto}
            saving={isSaving}
            onImageError={handleImageError}
            themeColor={theme.viewerTheme}
            versionOptions={photoVersions}
            currentVersionIndex={currentVersionIndex}
            onVersionChange={handleVersionChange}
            onDeleteVersion={handleDeleteVersion}
            versionsLoading={loadingVersions}
            currentUser={user}
            onNavigate={visiblePhotos.length > 1 ? handleNavigate : undefined}
          />
        </div>
      </div>

      {/* Comments Sidebar */}
      <CommentSidebar
        turbineId={turbine.id}
        isOpen={isCommentsOpen}
        onClose={() => setIsCommentsOpen(false)}
      />
    </div>
  )
}

export default OperatorPhotosScreen
